import { useRef, useState, useEffect } from 'react';
import Player from '@vimeo/player';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import i18n from 'i18next';

import { State, coreActions, coreSelectors } from '@gymcloud/core';
import { uiActions } from 'web-actions';
import { Button, Tooltip } from 'web-components';
import { SVG, Flex, Text } from 'web-styled';
import { OpenedTooltip } from 'web-types';
import { capitalizeFirstLetter } from 'web-utils';

import * as S from './styled';

// Types
type VideoResourceType =
  | 'exercises'
  | 'workoutExercises'
  | 'personalExercises'
  | 'workoutTemplates'
  | 'personalWorkouts'
  | 'programTemplates'
  | 'personalPrograms'
  | 'warmups'
  | 'cooldowns'
  | 'announcement';

interface VideoState {
  error?: boolean;
  available?: boolean;
  processing?: boolean;
}

interface Props {
  id?: number | string | null;
  src?: string | null;
  title?: string;
  height?: string;
  entityId?: number;
  readOnly?: boolean;
  isRounded?: boolean;
  isProgram?: boolean;
  requestType?: string;
  resourceType: VideoResourceType;
  openedTooltips?: OpenedTooltip[];
  buildQuery?: () => string;
  onAssign?: (video: any) => void;
  onUploaded?: (videos: any[]) => void;
  onDelete?: () => void;
}

// Utility functions
const EVENT_KEY_MAP = {
  exercises: 'exercise',
  workoutExercises: 'exercise',
  personalExercises: 'exercise',
  workoutTemplates: 'workout',
  personalWorkouts: 'workout',
  programTemplates: 'program',
  personalPrograms: 'program',
  warmups: 'workout',
  cooldowns: 'workout',
  announcement: 'announcement',
};

const getVideoAnalyticKey = (resourceType: VideoResourceType, isProgram?: boolean): string => {
  const eventKey = EVENT_KEY_MAP[resourceType];
  return eventKey === 'workout' && isProgram ? 'program_workout' : eventKey;
};

const getYouTubeId = (url?: string | null): string | null => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

const Video = ({
  id,
  src,
  title,
  height = '400px',
  entityId,
  readOnly = false,
  isRounded = false,
  isProgram = false,
  requestType,
  resourceType,
  openedTooltips = [],
  buildQuery,
  onAssign,
  onUploaded,
  onDelete,
}: Props) => {
  // Refs and state
  const iframe = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<Player | null>(null);
  const isVimeo = src && src.includes('vimeo');
  const [videoState, setVideoState] = useState<VideoState>({
    available: !isVimeo,
  });

  // Redux setup
  const { isPro } = useSelector(
    (state: State) => ({
      isPro: coreSelectors.getProfile('is_pro')(state),
    }),
    shallowEqual
  );
  const dispatch = useDispatch();

  // Variables
  const label = i18n.t(`short.label.${resourceType}`);
  const resourceEventKey = getVideoAnalyticKey(resourceType, isProgram);

  // Analytics tracking
  const analyticTrack = (eventKey: string, attrs?: any) => {
    dispatch({
      type: 'GC_ANALYTICS',
      eventKey,
      attrs,
    });
  };

  // Initialize video player
  useEffect(() => {
    if (id && !src) {
      setVideoState({ error: true, processing: true });
      return;
    }

    if (!src) {
      setVideoState({ error: true });
      return;
    }

    if (isVimeo) {
      initializeVimeoPlayer();
    } else {
      initializeYouTubePlayer();
    }

    return deInitializeVimeoPlayer;
  }, [id, src, isVimeo]);

  // WTF is going on?! Fix for player display issues
  useEffect(() => {
    const player = document.getElementById('player');
    if (player) {
      player.style.display = videoState.available ? 'flex' : 'none';
    }
  }, [videoState.available]);

  // Initialize Vimeo player
  const initializeVimeoPlayer = async () => {
    if (!iframe.current || !src) return;

    try {
      const player = new Player(iframe.current);
      playerRef.current = player;

      await player.ready();
      setVideoState({ available: true });

      // Set up event listeners
      player.on('play', () => analyticTrack('played_video'));
      player.on('pause', () => analyticTrack('paused_video'));
      player.on('ended', () => analyticTrack('ended_video'));
    } catch (error) {
      console.error('Error initializing Vimeo player:', error);
      setVideoState({ error: false, processing: true });
    }
  };

  const deInitializeVimeoPlayer = () => {
    if (playerRef.current) {
      playerRef.current.unload().catch(console.error);
      playerRef.current.off('play');
      playerRef.current.off('pause');
      playerRef.current.off('ended');
      playerRef.current.off('loaded');
      playerRef.current = null;
    }
  }

  // Initialize YouTube player
  const initializeYouTubePlayer = () => {
    setVideoState({ available: true });

    if (iframe.current && src) {
      iframe.current.contentWindow?.location.replace(src);
    }

    const createYouTubePlayer = () => {
      if (!iframe.current) {
        console.error('YouTube container element or video ID is not available');
        return;
      }

      const { YT } = window as any;
      if (!YT) return;

      new YT.Player('player', {
        height: '360',
        width: '640',
        videoId: getYouTubeId(src),
        events: {
          onStateChange: (event: any) => {
            const { PlayerState } = YT;

            switch (event.data) {
              case PlayerState.PLAYING:
                analyticTrack('played_video');
                break;
              case PlayerState.PAUSED:
                analyticTrack('played_paused');
                break;
              case PlayerState.ENDED:
                analyticTrack('ended_video');
                break;
              default:
                break;
            }
          },
        },
      });
    };

    // Load YouTube API if not already loaded
    if (!(window as any).YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      (window as any).onYouTubeIframeAPIReady = createYouTubePlayer;
      document.body.appendChild(tag);
    } else {
      createYouTubePlayer();
    }
  };

  // Handle tooltips
  const updateTooltips = () => {
    if (!openedTooltips?.length) return;

    const tooltip = openedTooltips.find(el => el.key === 'assign_video');
    if (!tooltip?.nextKey) return;

    dispatch(
      uiActions.setUiState({
        openedTooltips: [{ key: tooltip.nextKey }],
      })
    );
  };

  // Handle entity updates
  const handleUpdate = (payload: any, callbacks = {}) => {
    if (!entityId) return;

    dispatch(
      coreActions.requestUpdateEntity({
        id: entityId,
        payload,
        requestType,
        resourceType,
        callbacks,
      })
    );
  };

  // Delete video confirmation
  const handleDeleteVideo = () => {
    const confirmModal = {
      modal: {
        title: 'Delete Video?',
        bodyText: 'Are you sure you want to delete this video?',
        confirmText: 'Delete',
      },
      onConfirm: () => {
        deInitializeVimeoPlayer();
        setVideoState({ available: false });
        onDelete?.()
      },
      onConfirmAction: onDelete ? undefined : (
        entityId ? coreActions.requestUpdateEntity({
          id: entityId,
          payload: {
            video_id: null,
            video_url: null,
          },
          requestType,
          resourceType,
        }) : undefined
      ),
    };

    dispatch(uiActions.confirmModal(confirmModal));
  };

  // Update video
  const handleUpdateVideo = () => {
    const handleAssign = (video: any) => {
      handleUpdate({
        video_id: video.type === 'youtube' ? null : video.id,
        video_url: video.embed_url,
      });
    };

    const handleUploaded = (videos: any[]) => {
      handleUpdate({
        video_id: videos[0].id,
      });
    };

    const assignedId = (isVimeo && id) ? id : (src || '').split('/').pop();
    const query = buildQuery ? buildQuery() : (title || '');

    dispatch(
      uiActions.searchVideoModal({
        query,
        modal: {
          title: `Add ${capitalizeFirstLetter(label)} Video`,
          assignedId,
        },
        onCancel: updateTooltips,
        onAssign: (video: any) => {
          updateTooltips();
          analyticTrack(`added_video_${resourceEventKey}`, {
            id: video.id,
            url: video.embed_url,
            name: video.name,
          });
          (onAssign || handleAssign)(video);
        },
        onUploaded: (videos: any) => {
          updateTooltips();
          analyticTrack(`uploaded_video_${resourceEventKey}`, {
            id: videos[0].id,
            url: videos[0].src,
            name: videos[0].name,
          });
          (onUploaded || handleUploaded)(videos);
        },
      })
    );
  };

  // Assign button component
  const renderAssignButton = () => {
    if (readOnly) return null;

    if (openedTooltips?.some(el => el.key === 'assign_video')) {
      return (
        <Tooltip body="Click to add a video." toggleMode initialState>
          <Button appearance="primary" onClick={handleUpdateVideo}>
            Assign video
          </Button>
        </Tooltip>
      );
    }

    return (
      <Button appearance="primary" onClick={handleUpdateVideo}>
        Assign video
      </Button>
    );
  };

  return (
    <Flex column width="100%" height="100%" noPrint>
      <S.Video
        id="player"
        isVimeo={isVimeo}
      >
        <S.Frame
          src={src || 'about:blank'}
          ref={iframe}
          title={title}
          allowFullScreen
        />
      </S.Video>

      {videoState.error && (
        <S.Placeholder height={height} isRounded={isRounded}>
          <S.PlaceholderIcon>
            <SVG.PlayFilled size={30} />
          </S.PlaceholderIcon>
          {videoState.processing ? (
            <Text whiteSpace="pre-line">{i18n.t('video.processing')}</Text>
          ) : (
            <Text whiteSpace="pre-line">
              {i18n.t('video.not_assign', { type: label })}
              <br />
              {!isPro && 'Contact your Fitness Professional'}
            </Text>
          )}
          {renderAssignButton()}
        </S.Placeholder>
      )}

      {videoState.available && !readOnly && (
        <S.Controls>
          <Flex spacing={5} align="center">
            <Text>{isVimeo ? 'Vimeo' : 'Youtube'}</Text>
          </Flex>
          <Flex>
            <Button appearance="link" onClick={handleUpdateVideo}>
              REPLACE VIDEO
            </Button>
            <Button
              icon={<SVG.TrashAlt size={14} className="danger-color" />}
              appearance="link"
              onClick={handleDeleteVideo}
            />
          </Flex>
        </S.Controls>
      )}
    </Flex>
  );
};

export default Video;
